// src/components/LogoutNavbar.jsx
import logoImage from '../assets/images/Logo.svg';

import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Logout as LogoutIcon,
  ArrowBackIosNewRounded as BackIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  Inventory2 as LostAndFoundIcon,
  Event as EventIcon,
  Campaign as AnnouncementsIcon,
  MenuBook as ResourcesIcon,
  QuestionAnswer as DoubtsIcon,
  School as CollegeIcon,
  CalendarMonth as ManageEventsIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api'; // Import your API instance
import { getFullImageUrl } from '../middleware/Imagegetter'; // Import image URL helper

const LogoutNavbar = ({ sidebarItems = [], openMobileMenuSignal }) => {
  // State management
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    mySpace: true,
    campus: true,
    manage: true,
  });
  const [role, setRole] = useState(null);
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    image: '',
  });

  // Theme and navigation hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const canGoBack = (() => {
    try {
      const idx = window.history?.state?.idx;
      if (typeof idx === 'number') return idx > 0;
      return window.history.length > 1;
    } catch {
      return false;
    }
  })();

  const shouldShowBack = isMobile && location?.pathname !== '/' && canGoBack;

  const handleBack = () => {
    if (canGoBack) {
      navigate(-1);
      return;
    }
    navigate('/', { replace: true });
  };

  // Color configuration - matching the login navbar
  const colors = {
    primary: '#0D1C4F',
    secondary: '#1E3A8A',
    accent: '#10B981',
    light: '#F8FAFC',
    text: '#1F2937',
    textLight: '#6B7280',
    gradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
  };

  // Navigation items configuration
  const navigationItems = [
    // Add your navigation items here if needed
  ];

  // Logo configuration
  const logoConfig = {
    imageSrc: logoImage,
    imageAlt: 'PeerCampus Logo',
    title: 'PeerCampus'
  };

  // Fetch user data from API
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await api.get("users/me/");
        setUserData({
          name: response.username || '',
          email: response.email || '',
          image: response.image || '',
        });

        const userRole = response.role || response.user?.role;
        if (userRole) setRole(String(userRole).toLowerCase());
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };

    fetchUserDetails();
  }, []);

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get user avatar source
  const getUserAvatarSrc = () => {
    if (userData.image) {
      return getFullImageUrl(userData.image);
    }
    return null;
  };

  // Event handlers
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Allow pages to request opening the mobile drawer (e.g., Welcome "Explore" button)
  const prevOpenSignalRef = useRef(openMobileMenuSignal);
  useEffect(() => {
    if (!isMobile) return;
    if (openMobileMenuSignal === undefined || openMobileMenuSignal === null) return;
    if (prevOpenSignalRef.current === openMobileMenuSignal) return;
    prevOpenSignalRef.current = openMobileMenuSignal;
    setMobileOpen(true);
  }, [openMobileMenuSignal, isMobile]);

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  // ---------- Deep cookie clearing helpers ----------
  // Try expiring cookie name for given domain & path
  const expireCookie = (name, domain, path) => {
    const cookieStr = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path};`;
    try {
      // With domain (if provided)
      if (domain && domain !== 'undefined') {
        document.cookie = `${cookieStr} Domain=${domain};`;
      }
      // Without domain
      document.cookie = cookieStr;
    } catch (e) {
      // ignore
    }
  };

  // Generate domain variants to try (exact host, .host, parent domains, and undefined for no domain attr)
  const getDomainVariants = () => {
    const host = window.location.hostname || '';
    const parts = host.split('.').filter(Boolean);
    const variants = new Set();

    // Try no domain attribute (important for localhost)
    variants.add(undefined);

    if (host) {
      variants.add(host);
      variants.add(`.${host}`);
    }

    // Create parent domain combos like example.com, .example.com, app.example.com, .app.example.com, ...
    if (parts.length > 1) {
      for (let i = 0; i < parts.length - 1; i++) {
        const d = parts.slice(i).join('.');
        variants.add(d);
        variants.add(`.${d}`);
      }
    }

    return Array.from(variants);
  };

  // Generate path variants based on current pathname: '/', '/a', '/a/b', ...
  const getPathVariants = () => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const paths = new Set(['/']);
    let acc = '';
    segments.forEach(seg => {
      acc += `/${seg}`;
      paths.add(acc);
    });
    return Array.from(paths);
  };

  // Deep clear: remove ALL accessible cookies by iterating names × domains × paths
  const clearAllCookiesDeep = () => {
    try {
      const raw = document.cookie ? document.cookie.split('; ') : [];
      if (raw.length === 0) return;

      const names = raw.map(c => c.split('=')[0]).filter(Boolean);
      const domains = getDomainVariants();
      const paths = getPathVariants();

      names.forEach(name => {
        domains.forEach(domain => {
          paths.forEach(path => {
            expireCookie(name, domain, path);
          });
        });
      });

      // Also attempt a final pass with simple expiry (for safety)
      names.forEach(name => {
        try { document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`; } catch {}
      });
    } catch (e) {
      console.warn('Cookie clearing error:', e);
    }
  };
  // ---------- End cookie helpers ----------

  // Logout function (hoisted)
  async function handleLogout() {
    // console.log('Logging out user');
    handleUserMenuClose();
 
    // 1) Ask backend to invalidate server session/HttpOnly cookies (adjust URL if needed)
    try {
      await api.post('users/logout/');
    } catch (err) {
      console.warn('Logout API call failed (continuing client-side cleanup):', err);
    }

    // 2) Deep clear all accessible cookies
    try {
      clearAllCookiesDeep();
    } catch (e) {
      console.warn('Error clearing cookies:', e);
    }

    // 3) Clear storage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn('Storage clearing error:', e);
    }

    // 4) If you have app-level auth context / redux, dispatch logout here
    // e.g. authDispatch({ type: 'LOGOUT' });

    // 5) Hard replace the location so back button won't return to previous protected route
    try {
      window.location.replace('/');
    } catch (e) {
      navigate('/', { replace: true });
    }
  }

  // User menu items (call handleLogout)
  const userMenuItems = [
    {
      label: 'Logout',
      icon: <LogoutIcon fontSize="small" />,
      action: () => handleLogout(),
    },
  ];

  const handleNavItemClick = (item) => {
    item.action();
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };


  // Logo component
  const LogoComponent = () => {
    return (
      <Box
        component="a"
        href="/"
        onClick={(e) => {
          e.preventDefault();
          navigate('/');
        }}
        aria-label="PeerCampus - go to home"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          textDecoration: 'none',
          outline: 'none',
          border: 'none',
          background: 'transparent',
          p: 0,
          m: 0,
        }}
      >
        <Box
          component="img"
          src={logoConfig.imageSrc}
          alt={logoConfig.imageAlt}
          role="img"
          sx={{
            height: 48,
            width: 'auto',
            cursor: 'pointer',
            transition: 'transform 0.15s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
            '&:focus-visible': {
              outline: `3px solid ${colors.primary}33`,
              borderRadius: '6px'
            }
          }}
        />
      </Box>
    );
  };

  // Mobile drawer content
  const effectiveRole = (() => {
    if (role) return role;
    const path = (location?.pathname || '').toLowerCase();
    if (path.startsWith('/login/clubmember') || path.startsWith('/clubmember')) return 'club_member';
    if (path.startsWith('/login/student') || path.startsWith('/student') || path.startsWith('/your')) return 'student';
    if (path.startsWith('/login/admin') || path.startsWith('/admin')) return 'admin';
    return null;
  })();

  const portalTitle = (() => {
    switch (effectiveRole) {
      case 'club_member':
        return 'Club Member Portal';
      case 'student':
        return 'Student Portal';
      case 'admin':
      case 'college_admin':
        return 'Admin Portal';
      default:
        return 'Portal';
    }
  })();

  const getSectionRoute = (section) => {
    switch (effectiveRole) {
      case 'student':
        switch (section) {
          case 'lostfound':
            return '/student/lostfound';
          case 'yourlostfound':
            return '/student/yourlostfound';
          case 'events':
            return '/student/events';
          case 'announcements':
            return '/student/announcements';
          case 'know-your-college':
            return '/student/know-your-college';
          case 'skills':
            return '/student/skills';
          case 'yourskills':
            return '/student/yourskills';
          case 'doubts':
            return '/student/doubts';
          case 'yourdoubts':
            return '/student/yourdoubts';
          case 'eventmanagement':
            return '/eventmanagement';
          default:
            return '/login/student/home';
        }
      case 'club_member':
        switch (section) {
          case 'lostfound':
            return '/clubmember/lostfound';
          case 'yourlostfound':
            return '/clubmember/yourlostfound';
          case 'events':
            return '/clubmember/events';
          case 'announcements':
            return '/announcements';
          case 'know-your-college':
            return '/clubmember/know-your-college';
          case 'skills':
            return '/clubmember/skills';
          case 'yourskills':
            return '/clubmember/yourskills';
          case 'doubts':
            return '/clubmember/doubts';
          case 'yourdoubts':
            return '/clubmember/yourdoubts';
          case 'eventmanagement':
            return '/clubmember/eventmanagement';
          default:
            return '/login/clubmember/home';
        }
      case 'admin':
      case 'college_admin':
        switch (section) {
          case 'events':
            return '/events';
          case 'announcements':
            return '/announcements';
          case 'eventmanagement':
            return '/eventmanagement';
          default:
            return '/admin/dashboard';
        }
      default:
        switch (section) {
          case 'lostfound':
            return '/lostfound';
          case 'yourlostfound':
            return '/yourlostfound';
          case 'events':
            return '/events';
          case 'announcements':
            return '/announcements';
          case 'know-your-college':
            return '/know-your-college';
          case 'skills':
            return '/skills';
          case 'yourskills':
            return '/yourskills';
          case 'doubts':
            return '/doubts';
          case 'yourdoubts':
            return '/yourdoubts';
          case 'eventmanagement':
            return '/eventmanagement';
          default:
            return '/login';
        }
    }
  };

  const menuSections = {
    top: [
      {
        label: effectiveRole === 'admin' || effectiveRole === 'college_admin' ? 'Dashboard' : 'Profile',
        path:
          effectiveRole === 'student'
            ? '/login/student/home'
            : effectiveRole === 'club_member'
              ? '/login/clubmember/home'
              : '/admin/dashboard',
        action: () =>
          navigate(
            effectiveRole === 'student'
              ? '/login/student/home'
              : effectiveRole === 'club_member'
                ? '/login/clubmember/home'
                : '/admin/dashboard'
          ),
        icon:
          effectiveRole === 'admin' || effectiveRole === 'college_admin' ? <DashboardIcon /> : <PersonIcon />,
      },
    ],
    mySpace: [
      {
        label: 'My Lost & Found',
        path: getSectionRoute('yourlostfound'),
        action: () => navigate(getSectionRoute('yourlostfound')),
        icon: <LostAndFoundIcon />,
        roles: ['student', 'club_member'],
      },
      {
        label: 'My Resources',
        path: getSectionRoute('yourskills'),
        action: () => navigate(getSectionRoute('yourskills')),
        icon: <ResourcesIcon />,
        roles: ['student', 'club_member'],
      },
      {
        label: 'My Doubts',
        path: getSectionRoute('yourdoubts'),
        action: () => navigate(getSectionRoute('yourdoubts')),
        icon: <DoubtsIcon />,
        roles: ['student', 'club_member'],
      },
    ],
    campus: [
      {
        label: 'Lost & Found',
        path: getSectionRoute('lostfound'),
        action: () => navigate(getSectionRoute('lostfound')),
        icon: <LostAndFoundIcon />,
      },
      {
        label: 'Events',
        path: getSectionRoute('events'),
        action: () => navigate(getSectionRoute('events')),
        icon: <EventIcon />,
      },
      {
        label: 'Announcements',
        path: getSectionRoute('announcements'),
        action: () => navigate(getSectionRoute('announcements')),
        icon: <AnnouncementsIcon />,
      },
      {
        label: 'Know Your College',
        path: getSectionRoute('know-your-college'),
        action: () => navigate(getSectionRoute('know-your-college')),
        icon: <CollegeIcon />,
      },
      {
        label: 'Resources',
        path: getSectionRoute('skills'),
        action: () => navigate(getSectionRoute('skills')),
        icon: <ResourcesIcon />,
      },
      {
        label: 'Doubts',
        path: getSectionRoute('doubts'),
        action: () => navigate(getSectionRoute('doubts')),
        icon: <DoubtsIcon />,
      },
    ],
    manage: [
      {
        label: 'Manage Events',
        path: getSectionRoute('eventmanagement'),
        action: () => navigate(getSectionRoute('eventmanagement')),
        icon: <ManageEventsIcon />,
        roles: ['admin', 'college_admin', 'club_member'],
      },
      {
        label: 'Manage Announcements',
        path: '/admin/announcements',
        action: () => navigate('/admin/announcements'),
        icon: <AnnouncementsIcon />,
        roles: ['admin', 'college_admin'],
      },
      {
        label: 'Manage Students',
        path: '/admin/students',
        action: () => navigate('/admin/students'),
        icon: <DashboardIcon />,
        roles: ['admin', 'college_admin'],
      },
      {
        label: 'Manage Clubs',
        path: '/admin/clubs',
        action: () => navigate('/admin/clubs'),
        icon: <DashboardIcon />,
        roles: ['admin', 'college_admin'],
      },
    ],
  };

  const filterItemsByRole = (items) => {
    return (items || []).filter((item) => !item.roles || (effectiveRole && item.roles.includes(effectiveRole)));
  };

  const defaultSidebarItems = [
    ...filterItemsByRole(menuSections.top),
    ...filterItemsByRole(menuSections.mySpace),
    ...filterItemsByRole(menuSections.campus),
    ...filterItemsByRole(menuSections.manage),
  ];

  const normalizeItem = (item) => {
    if (!item) return null;
    const label = item.label || item.text;
    const path = item.path || item.to;
    const action =
      typeof item.action === 'function'
        ? item.action
        : typeof path === 'string' && path
          ? () => navigate(path)
          : undefined;
    if (!label || !path || !action) return null;
    return { label, path, action, icon: item.icon };
  };

  const isSettingsItem = (item) => {
    const label = String(item?.label || '').toLowerCase();
    const path = String(item?.path || '').toLowerCase();
    return label === 'settings' || path === '/settings';
  };

  const providedNormalized = (sidebarItems || [])
    .map(normalizeItem)
    .filter(Boolean)
    .filter((i) => !isSettingsItem(i));

  const providedByPath = new Map(
    providedNormalized.map((item) => [String(item.path).toLowerCase(), item])
  );

  // Canonical menu: always use the role-based default structure.
  // We only allow pages to override icon/action for matching paths.
  // We never append page-specific items (that caused duplicates/inconsistent labels).
  const mergedItems = defaultSidebarItems.map((item) => {
    const provided = providedByPath.get(String(item.path).toLowerCase());
    if (!provided) return item;
    return {
      ...item,
      icon: provided.icon || item.icon,
      action: provided.action || item.action,
    };
  });

  const isItemSelected = (to) => {
    const path = String(location?.pathname || '').toLowerCase();
    const target = String(to || '').toLowerCase();

    if (!target) return false;
    if (path === target) return true;

    // Keep highlighting for common nested/details routes
    if (target.endsWith('/events') && (path.startsWith(target) || path.includes('/event/'))) return true;
    if (target.endsWith('/lostfound') && (path.startsWith(target) || path.startsWith('/matcheditems'))) return true;
    if (target.endsWith('/yourlostfound') && path.startsWith(target)) return true;
    if (target.endsWith('/announcements') && path.startsWith(target)) return true;
    if (target.endsWith('/know-your-college') && path.startsWith(target)) return true;
    if (target.endsWith('/skills') && path.startsWith(target)) return true;
    if (target.endsWith('/yourskills') && path.startsWith(target)) return true;
    if (target.endsWith('/doubts') && path.startsWith(target)) return true;
    if (target.endsWith('/yourdoubts') && path.startsWith(target)) return true;
    if (target.endsWith('/eventmanagement') && path.startsWith(target)) return true;
    if (target.endsWith('/home') && path.startsWith(target)) return true;

    return false;
  };

  const getGroupKey = (item) => {
    const p = String(item?.path || '').toLowerCase();
    if (!p) return 'campus';
    if (p.includes('/login/student/home') || p.includes('/login/clubmember/home') || p.includes('/admin/dashboard')) return 'top';
    if (p.includes('/yourlostfound') || p.includes('/yourskills') || p.includes('/yourdoubts')) return 'mySpace';
    if (p.includes('eventmanagement') || p.startsWith('/admin/')) return 'manage';
    return 'campus';
  };

  const grouped = mergedItems.reduce(
    (acc, item) => {
      const key = getGroupKey(item);
      acc[key].push(item);
      return acc;
    },
    { top: [], mySpace: [], campus: [], manage: [] }
  );

  const topItems = grouped.top;
  const mySpaceItems = grouped.mySpace;
  const campusItems = grouped.campus;
  const manageItems = grouped.manage;

  const NavItem = ({ item, nested = false }) => {
    const selected = isItemSelected(item.path);
    return (
      <ListItem disablePadding sx={{ mx: 1, borderRadius: '8px', overflow: 'hidden', ...(nested ? { ml: 1.5, mr: 1 } : null) }}>
        <ListItemButton
          onClick={() => handleNavItemClick(item)}
          selected={selected}
          sx={{
            minHeight: 48,
            px: 1.5,
            borderRadius: '8px',
            '&:hover': { backgroundColor: `${colors.primary}08` },
            '&.Mui-selected': {
              backgroundColor: `${colors.primary}10`,
              borderLeft: `4px solid ${colors.accent}`,
              '&:hover': { backgroundColor: `${colors.primary}12` },
            },
            '&:focus-visible': {
              outline: `3px solid ${colors.primary}33`,
              bgcolor: `${colors.primary}04`,
            },
          }}
        >
          {item.icon ? (
            <ListItemIcon sx={{ minWidth: 36, color: selected ? colors.primary : colors.textLight }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={item.label}
            sx={{
              '& .MuiTypography-root': {
                fontWeight: selected ? 700 : 500,
                color: selected ? colors.primary : colors.text,
                fontSize: '1rem',
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const Section = ({ title, sectionKey, items }) => {
    if (!items || items.length === 0) return null;
    const open = Boolean(expandedSections?.[sectionKey]);
    return (
      <>
        <ListItem disablePadding sx={{ mt: 0.5 }}>
          <ListItemButton
            onClick={() => toggleSection(sectionKey)}
            sx={{
              mx: 1,
              borderRadius: '10px',
              minHeight: 48,
              px: 1.5,
              '&:hover': { backgroundColor: `${colors.primary}08` },
            }}
          >
            <ListItemText
              primary={title}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 700,
                  color: colors.text,
                  fontSize: '0.95rem',
                },
              }}
            />
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemButton>
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List sx={{ pt: 0.5 }}>
            {items.map((item) => (
              <NavItem key={item.label} item={item} nested />
            ))}
          </List>
        </Collapse>
      </>
    );
  };

  const drawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        backgroundColor: colors.light,
        overflowY: 'auto',
      }}
      role="presentation"
    >
      {/* Mobile drawer header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        p: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LogoComponent />
          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary }}>
            {logoConfig.title}
          </Typography>
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          aria-label="Close mobile menu"
          size="large"
          autoFocus
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User info in mobile drawer - Only name, email, image */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.primary}10` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: colors.accent,
              width: 48,
              height: 48,
              fontSize: '1.2rem',
              fontWeight: 600,
            }}
            src={getUserAvatarSrc()}
          >
            {getUserInitials(userData.name)}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text }}>
              {userData.name || 'Loading...'}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textLight, fontSize: '0.875rem' }}>
              {userData.email || 'Loading...'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Mobile navigation items */}
      <List sx={{ pt: 2 }}>
        {/* Sidebar items */}
        {mergedItems.length > 0 ? (
          <>
            <Typography variant="subtitle2" sx={{ px: 3, py: 1, color: colors.textLight }}>
              {portalTitle}
            </Typography>

            {topItems.map((item) => (
              <NavItem key={item.label} item={item} />
            ))}

            <Section title="My Space" sectionKey="mySpace" items={mySpaceItems} />
            <Section title="Campus" sectionKey="campus" items={campusItems} />
            <Section title="Management" sectionKey="manage" items={manageItems} />
          </>
        ) : null}

        {/* Mobile user menu items */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ px: 3, py: 1, color: colors.textLight }}>
          Account
        </Typography>
        {userMenuItems.map((item) => (
          <ListItem
            key={item.label}
            onClick={() => {
              item.action();
              setMobileOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                item.action();
                setMobileOpen(false);
              }
            }}
            role="button"
            tabIndex={0}
            sx={{
              cursor: 'pointer',
              mx: 1,
              borderRadius: '8px',
              minHeight: 48,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: `${colors.primary}08`,
              },
              '&:focus-visible': {
                outline: `3px solid ${colors.primary}33`,
                bgcolor: `${colors.primary}04`
              }
            }}
          >
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', color: colors.textLight }}>
              {item.icon}
            </Box>
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 500,
                  color: colors.text,
                  fontSize: '1rem'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        component="nav"
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: `0 6px 26px ${colors.primary}12`,
          left: 0,
          right: 0,
          top: 0,
          width: '100%',
          zIndex: theme.zIndex.appBar,
        }}
        aria-label="Main site navigation"
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              py: 1,
              minHeight: '70px'
            }}
          >
            {/* Logo Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {shouldShowBack && (
                <IconButton
                  onClick={handleBack}
                  aria-label="Go back"
                  size="large"
                  edge="start"
                  sx={{
                    color: colors.primary,
                    '&:hover': { backgroundColor: `${colors.primary}08` },
                    '&:focus-visible': { outline: `3px solid ${colors.primary}33`, borderRadius: '10px' },
                  }}
                >
                  <BackIcon fontSize="small" />
                </IconButton>
              )}
              <LogoComponent />

              {/* Title */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { sm: '1.5rem', md: '2rem' },
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate('/')}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/');
                    }
                  }}
                  role="link"
                  aria-label="Go to home"
                >
                  {logoConfig.title}
                </Typography>
                <Chip
                  label="AI-Powered"
                  size="small"
                  sx={{
                    backgroundColor: colors.accent,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
              </Box>

              {/* Mobile title */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/')}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate('/');
                    }
                  }}
                  role="link"
                  aria-label="Go to home"
                >
                  {logoConfig.title}
                </Typography>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center' }}>
              {navigationItems.map((item) => {
                const isActive = location?.pathname === item.path;
                return (
                  <Button
                    key={item.label}
                    onClick={() => handleNavItemClick(item)}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={`Navigate to ${item.label}`}
                    sx={{
                      color: isActive ? colors.primary : colors.textLight,
                      cursor: 'pointer',
                      fontWeight: isActive ? 600 : 500,
                      position: 'relative',
                      textTransform: 'none',
                      minWidth: 'auto',
                      padding: '8px 12px',
                      minHeight: 44,
                      fontSize: '1rem',
                      '&:hover': {
                        color: colors.primary,
                        '&::after': {
                          width: '100%'
                        }
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -6,
                        left: 0,
                        width: isActive ? '100%' : 0,
                        height: 2,
                        backgroundColor: colors.primary,
                        transition: 'width 0.25s ease'
                      },
                      '&:focus-visible': {
                        outline: `3px solid ${colors.primary}33`,
                        borderRadius: '6px',
                        boxShadow: `0 0 0 4px ${colors.primary}10`
                      }
                    }}
                  >
                    <Box component="span" sx={{ fontSize: '1rem' }}>{item.label}</Box>
                  </Button>
                );
              })}

              {/* User Profile Menu - Only image, name, email */}
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <Button
                  onClick={handleUserMenuOpen}
                  aria-label="User menu"
                  aria-controls="user-menu"
                  aria-haspopup="true"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 12px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    color: colors.text,
                    minHeight: 44,
                    '&:hover': {
                      backgroundColor: `${colors.primary}08`,
                    },
                    '&:focus-visible': {
                      outline: `3px solid ${colors.primary}33`,
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: colors.accent,
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}
                    src={getUserAvatarSrc()}
                  >
                    {getUserInitials(userData.name)}
                  </Avatar>
                  <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {userData.name ? userData.name.split(' ')[0] : 'User'}
                    </Typography>
                  </Box>
                </Button>

                <Menu
                  id="user-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleUserMenuClose}
                  onClick={handleUserMenuClose}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1.5,
                      minWidth: 200,
                      borderRadius: '12px',
                      overflow: 'visible',
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    }
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {/* User info in dropdown - Only name and email */}
                  <MenuItem sx={{ cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}>
                    <Box sx={{ px: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.text }}>
                        {userData.name || 'Loading...'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.textLight, fontSize: '0.875rem' }}>
                        {userData.email || 'Loading...'}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />

                  {/* User menu items */}
                  {userMenuItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      onClick={item.action}
                      sx={{
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: `${colors.primary}08`,
                        }
                      }}
                    >
                      <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', color: colors.textLight }}>
                        {item.icon}
                      </Box>
                      <Typography variant="body2">
                        {item.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>

            {/* Mobile menu button */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                color="primary"
                aria-label="Open mobile menu"
                aria-haspopup="true"
                aria-controls="mobile-menu"
                edge="start"
                onClick={handleDrawerToggle}
                aria-expanded={mobileOpen ? 'true' : 'false'}
                sx={{
                  color: colors.primary,
                  '&:hover': {
                    backgroundColor: `${colors.primary}08`,
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${colors.primary}33`,
                    borderRadius: '8px'
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Box component="nav" aria-label="Mobile navigation">
        <Drawer
          id="mobile-menu"
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
            },
          }}
          PaperProps={{
            'aria-label': 'Mobile navigation drawer',
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Spacer for fixed navbar */}
      <Box sx={{ height: '70px' }} />
    </>
  );
};

export default LogoutNavbar;
